import type { ImageCompressionPort } from '@src/modules/platform/local-media/domain/ports/ImageCompressionPort.ts';
import { CanvasImageCompressionAdapter } from '@src/modules/platform/local-media/infrastructure/driven/CanvasImageCompressionAdapter.ts';

export type PdfPreparedImage = {
  dataUrl: string | null;
  bytes: Uint8Array | null;
  format: 'JPEG' | 'PNG';
};

/** ~2× IMAGE_MIN(100pt) cell — enough for print, tiny vs camera JPEG. */
const PDF_IMAGE_MAX_EDGE = 280;
const PDF_MAP_MAX_EDGE = 480;

const defaultCompression: ImageCompressionPort = new CanvasImageCompressionAdapter();

export function emptyPdfImage(): PdfPreparedImage {
  return { dataUrl: null, bytes: null, format: 'JPEG' };
}

export function hasPdfImagePayload(
  img: { dataUrl?: string | null; bytes?: Uint8Array | null } | null | undefined,
): boolean {
  if (img?.bytes != null && img.bytes.byteLength > 0) return true;
  return typeof img?.dataUrl === 'string' && img.dataUrl.trim().length > 0;
}

async function blobToPdfBytes(blob: Blob): Promise<Uint8Array> {
  return new Uint8Array(await blob.arrayBuffer());
}

function formatFromMime(mime: string): 'JPEG' | 'PNG' {
  return mime.includes('png') ? 'PNG' : 'JPEG';
}

/** Downscale + keep raw bytes (no dataUrl) for pdf-lib embed. */
export async function preparePdfImageFromBlob(
  blob: Blob,
  opts: { maxEdge?: number; compression?: ImageCompressionPort } = {},
): Promise<PdfPreparedImage> {
  const compression = opts.compression ?? defaultCompression;
  const maxEdge = opts.maxEdge ?? PDF_IMAGE_MAX_EDGE;
  // Firma PNG → JPEG OK (fondo blanco). Sin DOM (tests) compress no-op.
  const shrunk = await compression.compress(blob, {
    maxEdge,
    quality: 0.72,
    mimeType: 'image/jpeg',
    skipBelowBytes: 0,
  });
  const bytes = await blobToPdfBytes(shrunk);
  return { dataUrl: null, bytes, format: formatFromMime(shrunk.type || blob.type) };
}

/** Map composite (or legacy dataUrl) → bytes when possible. */
export async function preparePdfImageFromDataUrl(
  dataUrl: string,
  format: 'JPEG' | 'PNG' = 'JPEG',
  opts: { maxEdge?: number; compression?: ImageCompressionPort } = {},
): Promise<PdfPreparedImage> {
  try {
    const res = await fetch(dataUrl);
    if (!res.ok) return { dataUrl, bytes: null, format };
    const blob = await res.blob();
    return preparePdfImageFromBlob(blob, {
      maxEdge: opts.maxEdge ?? PDF_MAP_MAX_EDGE,
      compression: opts.compression,
    });
  } catch {
    return { dataUrl, bytes: null, format };
  }
}

export async function resolvePdfImageBytes(
  image: { dataUrl?: string | null; bytes?: Uint8Array | null; format: 'JPEG' | 'PNG' } | null | undefined,
): Promise<{ bytes: Uint8Array; format: 'JPEG' | 'PNG' } | null> {
  if (!image) return null;
  if (image.bytes != null && image.bytes.byteLength > 0) {
    return { bytes: image.bytes, format: image.format };
  }
  const raw = image.dataUrl?.split(',')[1];
  if (!raw) return null;
  try {
    const bytes = Uint8Array.from(atob(raw), (c) => c.charCodeAt(0));
    return { bytes, format: image.format };
  } catch {
    return null;
  }
}
